import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinkClass = scrolled
    ? 'text-foreground-600 hover:text-foreground-950'
    : 'text-white/80 hover:text-white';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className={`font-heading text-lg md:text-xl font-bold transition-colors duration-300 whitespace-nowrap ${scrolled ? 'text-foreground-950' : 'text-white'}`}>
                OOPBuy Spreadsheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6" ref={dropdownRef}>
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${navLinkClass}`}
              >
                Startseite
              </Link>

              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer px-3 py-2 rounded-full ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <span>Kategorien</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-[720px] bg-white rounded-2xl shadow-lg border border-background-200/70 p-5 grid grid-cols-3 gap-2 z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-100 transition-colors group cursor-pointer"
                      >
                        <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                          <img
                            src={cat.imageUrl}
                            alt={cat.name}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative group">
                <button
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${navLinkClass}`}
                >
                  Shopping Guide
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </button>
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-lg border border-background-200/70 p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <Link to="/artikel/oopbuy-spreadsheet-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-50 rounded-xl transition-colors">Kompletter Guide</Link>
                  <Link to="/artikel/oopbuy-qc-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-50 rounded-xl transition-colors">QC Guide</Link>
                  <Link to="/artikel/oopbuy-versand-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-50 rounded-xl transition-colors">Versand Guide</Link>
                  <Link to="/artikel/oopbuy-verkaeufer-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-50 rounded-xl transition-colors">Verkäufer Guide</Link>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                OOPBuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-foreground-950 text-white text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>

              <button
                onClick={() => setMobileOpen(true)}
                className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                  scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-menu-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-[400px] bg-white overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-100">
              <span className="font-heading text-lg font-bold text-foreground-950">Menü</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="p-4">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-background-50 transition-colors mb-2"
              >
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-primary-500 text-lg"></i>
                </div>
                <span className="font-semibold text-sm text-foreground-900">Startseite</span>
              </Link>

              <div className="my-4 pt-4 border-t border-background-100">
                <p className="text-xs text-foreground-400 font-medium uppercase tracking-wider mb-3 px-3">Kategorien</p>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-50 transition-colors group cursor-pointer"
                    >
                      <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                      <div className="flex items-center gap-2 flex-1">
                        <i className={`${cat.icon} text-sm text-foreground-400`}></i>
                        <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300"></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="my-4 pt-4 border-t border-background-100">
                <p className="text-xs text-foreground-400 font-medium uppercase tracking-wider mb-3 px-3">Shopping Guide</p>
                <div className="space-y-1">
                  <Link to="/artikel/oopbuy-spreadsheet-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-50 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                      <i className="ri-book-open-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">Kompletter Guide</span>
                  </Link>
                  <Link to="/artikel/oopbuy-qc-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-50 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                      <i className="ri-check-double-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">QC Guide</span>
                  </Link>
                  <Link to="/artikel/oopbuy-versand-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-50 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                      <i className="ri-truck-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">Versand Guide</span>
                  </Link>
                  <Link to="/artikel/oopbuy-verkaeufer-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-50 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
                      <i className="ri-user-star-line text-foreground-500"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">Verkäufer Guide</span>
                  </Link>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-background-100">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  OOPBuy Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}